import { useEffect, useMemo, useState } from "react";
import { buildGoogleOAuthStartUrl } from "@/lib/services/authService";
import {
  normalizeEmail,
  toTitleCaseTr,
  trimBlurValue,
} from "@/lib/utils/formFormatters";
import {
  buildRegisterPayload,
  checkPublicRegisterEmailAvailability,
} from "@/modules/provisioning";
import {
  createConversionEventId,
  GROWTH_EVENT_NAMES,
  trackGrowthEvent,
} from "@/lib/analytics/growthAnalytics";
import {
  growthEventAttribution,
  markPendingGoogleSignUp,
} from "@/lib/analytics/growthAttribution";
import {
  getPasswordStrengthMessage,
  validateRegisterField,
} from "./registerFormUtils";
import type {
  FieldFeedback,
  FieldName,
  FieldState,
  UsageMode,
} from "./registerFormTypes";

type AuthActions = {
  register: (
    _payload: ReturnType<typeof buildRegisterPayload>
  ) => Promise<unknown>;
  clearError: () => void;
};

const emptyFields = (): FieldState => ({
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  organizationName: "",
  termsAccepted: false,
});

function useFeedback() {
  const [fieldErrors, setFieldErrors] = useState<FieldFeedback>({});
  const [fieldSuccess, setFieldSuccess] = useState<FieldFeedback>({});
  const setFeedback = (
    setter: typeof setFieldErrors,
    field: FieldName,
    message: string | null
  ) =>
    setter((previous) => {
      const next = { ...previous };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });
  return {
    fieldErrors,
    fieldSuccess,
    setFieldErrors,
    setFieldSuccess,
    setErrorForField: (field: FieldName, message: string | null) =>
      setFeedback(setFieldErrors, field, message),
    setSuccessForField: (field: FieldName, message: string | null) =>
      setFeedback(setFieldSuccess, field, message),
  };
}

function useEmailAvailability(
  email: string,
  usageMode: UsageMode,
  password: string,
  setErrorForField: (_field: FieldName, _message: string | null) => void,
  setSuccessForField: (_field: FieldName, _message: string | null) => void
) {
  const [emailAvailabilityMessage, setEmailAvailabilityMessage] = useState("");
  const debouncedEmail = useMemo(() => email.trim(), [email]);
  useEffect(() => {
    if (
      !debouncedEmail ||
      validateRegisterField("email", debouncedEmail, usageMode, password)
    )
      return;
    const timeoutId = window.setTimeout(async () => {
      try {
        const result =
          await checkPublicRegisterEmailAvailability(debouncedEmail);
        const message = result.message || "Bu e-posta adresi kullanılabilir.";
        setEmailAvailabilityMessage(message);
        setErrorForField("email", null);
        setSuccessForField("email", message);
      } catch (availabilityError) {
        const message =
          availabilityError instanceof Error
            ? availabilityError.message
            : "E-posta uygunluk kontrolü tamamlanamadı. Lütfen tekrar deneyiniz.";
        setEmailAvailabilityMessage("");
        setErrorForField("email", message);
        setSuccessForField("email", null);
      }
    }, 500);
    return () => window.clearTimeout(timeoutId);
  }, [
    debouncedEmail,
    password,
    setErrorForField,
    setSuccessForField,
    usageMode,
  ]);
  return { emailAvailabilityMessage, setEmailAvailabilityMessage };
}

function useFormFields(organizationNameHint: string) {
  const [usageMode, setUsageMode] = useState<UsageMode>("BUSINESS");
  const [fieldValues, setFieldValues] = useState<FieldState>(() => ({
    ...emptyFields(),
    organizationName: organizationNameHint,
  }));
  const firstName = String(fieldValues.firstName);
  const lastName = String(fieldValues.lastName);
  const email = String(fieldValues.email);
  const password = String(fieldValues.password);
  const confirmPassword = String(fieldValues.confirmPassword);
  const organizationName = String(fieldValues.organizationName);
  const termsAccepted = Boolean(fieldValues.termsAccepted);
  return {
    usageMode,
    setUsageMode,
    fieldValues,
    setFieldValues,
    firstName,
    lastName,
    email,
    password,
    confirmPassword,
    organizationName,
    termsAccepted,
  };
}

type FieldsModel = ReturnType<typeof useFormFields>;
type FeedbackModel = ReturnType<typeof useFeedback>;
type AvailabilityModel = ReturnType<typeof useEmailAvailability>;
type ValidationContext = {
  fields: FieldsModel;
  feedback: FeedbackModel;
  availability: AvailabilityModel;
};

function validateAndSetField(
  context: ValidationContext,
  field: FieldName,
  value: string | boolean,
  nextUsageMode = context.fields.usageMode,
  nextPassword = context.fields.password
) {
  const message = validateRegisterField(
    field,
    value,
    nextUsageMode,
    nextPassword
  );
  context.feedback.setErrorForField(field, message);
  if (message) {
    context.feedback.setSuccessForField(field, null);
    return message;
  }
  const text = String(value).trim();
  const success =
    field === "email"
      ? context.availability.emailAvailabilityMessage ||
        "E-posta formatı uygun görünüyor."
      : field === "password" && text
        ? "Şifre kullanım için hazır görünüyor."
        : field === "termsAccepted" && value
          ? "Sözleşme onayı alındı."
          : field !== "termsAccepted" && text
            ? "Bu alan hazır görünüyor."
            : null;
  context.feedback.setSuccessForField(field, success);
  return null;
}

function createFieldChangeHandler(
  context: ValidationContext,
  setValidationError: (_message: string) => void
) {
  return (field: FieldName, value: string | boolean) => {
    setValidationError("");
    const normalizedValue =
      field === "email" ? normalizeEmail(String(value || "")) : value;
    context.fields.setFieldValues((previous) => ({
      ...previous,
      [field]: normalizedValue,
    }));
    if (field === "email") context.availability.setEmailAvailabilityMessage("");
    const nextPassword =
      field === "password"
        ? String(normalizedValue || "")
        : context.fields.password;
    validateAndSetField(
      context,
      field,
      normalizedValue,
      context.fields.usageMode,
      nextPassword
    );
    if (field === "password" && context.fields.confirmPassword)
      validateAndSetField(
        context,
        "confirmPassword",
        context.fields.confirmPassword,
        context.fields.usageMode,
        nextPassword
      );
  };
}

function createFieldBlurHandler(context: ValidationContext) {
  return (field: FieldName) => {
    let nextValue = context.fields.fieldValues[field];
    if (typeof nextValue === "string") {
      nextValue =
        field === "firstName" ||
        field === "lastName" ||
        field === "organizationName"
          ? toTitleCaseTr(nextValue)
          : field === "email"
            ? normalizeEmail(nextValue)
            : trimBlurValue(nextValue);
      context.fields.setFieldValues((previous) => ({
        ...previous,
        [field]: nextValue,
      }));
    }
    validateAndSetField(
      context,
      field,
      nextValue,
      context.fields.usageMode,
      context.fields.password
    );
  };
}

function createUsageModeHandler(context: ValidationContext) {
  return (nextMode: string) => {
    const nextUsageMode: UsageMode =
      nextMode === "INDIVIDUAL" ? "INDIVIDUAL" : "BUSINESS";
    context.fields.setUsageMode(nextUsageMode);
    if (nextUsageMode === "INDIVIDUAL") {
      context.feedback.setErrorForField("organizationName", null);
      context.feedback.setSuccessForField("organizationName", null);
      return;
    }
    validateAndSetField(
      context,
      "organizationName",
      context.fields.organizationName,
      nextUsageMode,
      context.fields.password
    );
  };
}

function validateAllFields(context: ValidationContext) {
  const { fields } = context;
  const validations: Array<[FieldName, string | boolean]> = [
    ["firstName", fields.firstName],
    ["lastName", fields.lastName],
    ["email", fields.email],
    ["password", fields.password],
    ["confirmPassword", fields.confirmPassword],
    ["termsAccepted", fields.termsAccepted],
  ];
  if (fields.usageMode === "BUSINESS")
    validations.push(["organizationName", fields.organizationName]);
  return validations.every(
    ([field, value]) => !validateAndSetField(context, field, value)
  );
}

type SubmitContext = ValidationContext & {
  auth: AuthActions;
  registrationCompleted: boolean;
  setRegistrationCompleted: (_completed: boolean) => void;
  setSuccessMessage: (_message: string) => void;
  setValidationError: (_message: string) => void;
};

function createSubmitHandler(context: SubmitContext) {
  return async (event: React.FormEvent) => {
    event.preventDefault();
    if (context.registrationCompleted) return;
    context.setValidationError("");
    context.setSuccessMessage("");
    context.auth.clearError();
    if (!validateAllFields(context)) {
      context.setValidationError(
        "Lütfen işaretli alanları kontrol ediniz. Eksik veya hatalı bilgileri tamamladıktan sonra tekrar deneyebilirsiniz."
      );
      return;
    }
    const conversionEventId = createConversionEventId("signup");
    trackGrowthEvent(GROWTH_EVENT_NAMES.FORM_START, {
      conversion_event_id: conversionEventId,
      form_name: "register",
      method: "email",
      ...growthEventAttribution(window.location.pathname),
    });
    try {
      const registeredEmail = context.fields.email;
      const result = (await context.auth.register(
        buildRegisterPayload({
          email: context.fields.email,
          password: context.fields.password,
          firstName: context.fields.firstName,
          lastName: context.fields.lastName,
          organizationName: context.fields.organizationName,
          termsAccepted: context.fields.termsAccepted,
          usageMode: context.fields.usageMode,
        })
      )) as { requiresVerification?: boolean; message?: string } | void;
      trackGrowthEvent(GROWTH_EVENT_NAMES.SIGN_UP, {
        conversion_event_id: conversionEventId,
        form_name: "register",
        method: "email",
        ...growthEventAttribution(window.location.pathname),
      });
      if (!result?.requiresVerification) {
        window.location.href = "/dashboard";
        return;
      }
      context.fields.setFieldValues(emptyFields());
      context.feedback.setFieldErrors({});
      context.feedback.setFieldSuccess({});
      context.fields.setUsageMode("BUSINESS");
      context.setRegistrationCompleted(true);
      context.setSuccessMessage(
        result.message ||
          "Kayıt tamamlandı. E-posta doğrulama bağlantısını açtıktan sonra doğrudan panelinize geçebilirsiniz."
      );
      setTimeout(() => {
        window.location.href = `/verify-email?email=${encodeURIComponent(registeredEmail)}`;
      }, 800);
    } catch {
      /* Error authStore üzerinden normalize edilip UI'da gösterilir. */
    }
  };
}

function createGoogleRegisterHandler(context: SubmitContext) {
  return () => {
    if (
      context.fields.usageMode === "BUSINESS" &&
      !context.fields.organizationName.trim()
    ) {
      context.setValidationError(
        "Google ile devam etmeden önce şirket adınızı giriniz. Böylece hesabınızı doğru organizasyonla başlatabiliriz."
      );
      context.feedback.setErrorForField(
        "organizationName",
        "Şirket kullanımında şirket adı zorunludur. Devam etmek için şirket adınızı giriniz."
      );
      return;
    }
    context.setValidationError("");
    context.auth.clearError();
    const conversionEventId = createConversionEventId("signup");
    trackGrowthEvent(GROWTH_EVENT_NAMES.FORM_START, {
      conversion_event_id: conversionEventId,
      form_name: "register",
      method: "google",
      ...growthEventAttribution(window.location.pathname),
    });
    markPendingGoogleSignUp(conversionEventId);
    window.location.href = buildGoogleOAuthStartUrl({
      mode: "register",
      usageMode: context.fields.usageMode,
      organizationName:
        context.fields.usageMode === "BUSINESS"
          ? context.fields.organizationName.trim()
          : undefined,
      termsAccepted: true,
      returnTo: "/dashboard",
      conversionEventId,
    });
  };
}

export function useRegisterForm(
  initialUsageMode: UsageMode,
  organizationNameHint: string,
  auth: AuthActions
) {
  const fields = useFormFields(organizationNameHint);
  const feedback = useFeedback();
  const availability = useEmailAvailability(
    fields.email,
    fields.usageMode,
    fields.password,
    feedback.setErrorForField,
    feedback.setSuccessForField
  );
  const [registerMethod, setRegisterMethod] = useState<"email" | "google">(
    "email"
  );
  const [registrationCompleted, setRegistrationCompleted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [validationError, setValidationError] = useState("");
  const { setUsageMode } = fields;
  useEffect(() => {
    setUsageMode(initialUsageMode);
  }, [initialUsageMode, setUsageMode]);

  const validationContext = { fields, feedback, availability };
  const submitContext = {
    ...validationContext,
    auth,
    registrationCompleted,
    setRegistrationCompleted,
    setSuccessMessage,
    setValidationError,
  };
  const handleFieldChange = createFieldChangeHandler(
    validationContext,
    setValidationError
  );
  const handleFieldBlur = createFieldBlurHandler(validationContext);
  const handleUsageModeChange = createUsageModeHandler(validationContext);
  const handleSubmit = createSubmitHandler(submitContext);
  const handleGoogleRegister = createGoogleRegisterHandler(submitContext);

  return {
    ...fields,
    ...feedback,
    ...availability,
    registerMethod,
    setRegisterMethod,
    registrationCompleted,
    successMessage,
    validationError,
    setValidationError,
    passwordStrengthMessage: getPasswordStrengthMessage(fields.password),
    handleFieldChange,
    handleFieldBlur,
    handleUsageModeChange,
    handleSubmit,
    handleGoogleRegister,
  };
}

export type RegisterFormModel = ReturnType<typeof useRegisterForm>;
